import { useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PROJECT_INDUSTRY_LABELS, PROJECT_TYPE_LABELS } from '@/types'
import type { ProjectSortColumn } from '@/types'
import { ClearFiltersIcon } from '@/components/icons'
import { useProjectClients } from '@/hooks'
import { ALL_CLIENTS } from '@/hooks/useProjectFilters'
import { Search, X } from 'lucide-react'

interface ProjectsFilterBarProps {
  filters: { search: string; type: string; industry: string; client: string }
  onFilterChange: (key: 'search' | 'type' | 'industry' | 'client', value: string) => void
  sortBy: ProjectSortColumn | null
  onClearAll: () => void
}

export function ProjectsFilterBar({ filters, onFilterChange, sortBy, onClearAll }: ProjectsFilterBarProps) {
  const { data: clients = [], isSuccess: clientsLoaded } = useProjectClients()
  const hasActiveSort = sortBy !== null
  const hasActiveFilters = filters.search !== '' || filters.type !== 'all' || filters.industry !== 'all' || filters.client !== ALL_CLIENTS || hasActiveSort

  useEffect(() => {
    if (clientsLoaded && filters.client !== ALL_CLIENTS && !clients.includes(filters.client))
      onFilterChange('client', ALL_CLIENTS)
  }, [clientsLoaded, clients, filters.client, onFilterChange])

  return (
    <div className="flex flex-1 items-center gap-3">
      <div className="relative max-w-sm flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search projects..."
          value={filters.search}
          onChange={(e) => onFilterChange('search', e.target.value)}
          className="pl-9 pr-9"
          aria-label="Search projects"
        />
        {filters.search && (
          <button
            type="button"
            onClick={() => onFilterChange('search', '')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <Select
        value={filters.type}
        onValueChange={(value) => onFilterChange('type', value)}
      >
        <SelectTrigger className="w-[160px]" aria-label="Filter by type">
          <SelectValue placeholder="Type" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Types</SelectItem>
          {Object.entries(PROJECT_TYPE_LABELS).map(([value, label]) => (
            <SelectItem key={value} value={value}>{label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.industry}
        onValueChange={(value) => onFilterChange('industry', value)}
      >
        <SelectTrigger className="w-[180px]" aria-label="Filter by industry">
          <SelectValue placeholder="Industry" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Industries</SelectItem>
          {Object.entries(PROJECT_INDUSTRY_LABELS).map(([value, label]) => (
            <SelectItem key={value} value={value}>{label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.client}
        onValueChange={(value) => onFilterChange('client', value)}
      >
        <SelectTrigger className="w-[180px]" aria-label="Filter by client">
          <SelectValue placeholder="Client" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_CLIENTS}>All Clients</SelectItem>
          {clients.map((client) => (
            <SelectItem key={client} value={client}>{client}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        type="button"
        variant="ghost"
        onClick={onClearAll}
        disabled={!hasActiveFilters}
        className="flex items-center gap-1.5 text-sm font-medium"
        aria-label="Clear all filters"
      >
        <ClearFiltersIcon className="h-4 w-4" />
        Clear All
      </Button>

    </div>
  )
}
